import { useContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { nanoid } from 'nanoid';
import Bluebird from 'bluebird';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import * as ApiModels from '@typings/api-models';
import useNoodleApi from '@hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import { useParams } from 'next/navigation';
import * as tsClient from '@tsClient';
import * as format from '@format';
import { useFeatureFlag } from '@/providers/FeatureFlags';
import { useQueryState } from 'nuqs';
import { useEventsContext } from '@providers/Events';
import Emoji from '@components/DesignLibrary/Atoms/Emoji';
import LanguagePill from '@/components/LanguagePill';

import {
  type HookResponse,
  type ResourceType,
  type ListItem,
  type ListViewRegularItem,
  type TableCell,
  type TableRow,
  type TableColumn,
  addHeaderKeyToCell,
} from './common';
import InvoiceProgress from './InvoiceProgress';
import InitiatedByPill from './InitiatedByPill';

type ThisUserWorkflow = Awaited<ReturnType<typeof tsClient.creators.getUserWorkflows>>['items'][number];

type PendingClientTasksFilter = 'custom-terms' | 'document-request-user' | 'form-request' | 'invoice';

interface StatusProperty {
  label: string;
  color: string;
}

const StatusToBadge: Record<string, StatusProperty> = {
  paused: { color: 'var( --color-workflow-paused)', label: 'Paused' },
};

export const formatUserWorkflowContext = (userWorkflowContext: Pick<ApiModels.UserWorkflowContext, 'label' | 'value' | 'type'>[]): string => {
  let returnString = '';
  userWorkflowContext.forEach((context, index) => {
    returnString = returnString.concat(`${context.label}: ${context.value}${index === userWorkflowContext.length - 1 ? '' : ', '}`);
  });
  return returnString;
};

export const statusToBadge = ({
  pausedUntil,
  status,
}: {
  pausedUntil: string | null;
  status: Pick<ApiModels.CustomStatus, 'colour' | 'title'> | undefined;
}): ListViewRegularItem['badge'] | undefined => {
  if (pausedUntil) {
    return StatusToBadge.paused;
  }
  if (status) {
    return { color: status.colour, label: status.title };
  }
  return undefined;
};

const TABLE_COLUMNS: TableColumn[] = [
  {
    columnId: 'workflow.name',
    key: 'workflow',
    label: 'Workflow Title',
  },
  {
    columnId: 'status',
    key: 'status',
    label: 'Workflow Status',
  },
  {
    key: 'assignees',
    label: 'Assignees',
  },
  {
    columnId: 'initiatedBy.name',
    key:'initiatedBy',
    label: 'Initiated By',
  },
  {
    columnId: 'person.name',
    key: 'contact',
    label: 'Contact',
  },
  {
    key: 'balance',
    label: 'Balance',
  },
  {
    key: 'context',
    label: 'Context',
  },
  {
    key: 'pending-client-tasks',
    label: 'Pending Client Tasks',
  },
  {
    columnId: 'progress',
    key: 'progress',
    label: 'Progress',
  },
  {
    columnId: 'lastActivityAt',
    key: 'recent-activity',
    label: 'Recent activity',
  },
  {
    columnId: 'createdAt',
    key: 'created-at',
    label: 'Created at',
  },
];

const curriedAddHeaderKeyToCell = addHeaderKeyToCell(TABLE_COLUMNS);

const userWorkflowToTableRow = ({
  hasAssignedTasksByUserWorkflow,
  userWorkflow,
  userWorkflowStatuses,
}: {
  hasAssignedTasksByUserWorkflow: Record<string, boolean | 'fetching'>;
  userWorkflow: Awaited<ReturnType<typeof tsClient.creators.getUserWorkflows>>['items'][number];
  userWorkflowStatuses: Array<ApiModels.CustomStatus> | null;
}): TableRow => {
  const userWorkflowStatus = userWorkflowStatuses?.find(status => status.slug === userWorkflow.status) ?? undefined;
  const statusBadge = statusToBadge({ pausedUntil: userWorkflow.pausedUntil, status: userWorkflowStatus });
  const progress = userWorkflow.status === 'data-collection' || userWorkflow.pausedUntil ? userWorkflow.progress * 100 : undefined;
  const hasAssignedTasks = hasAssignedTasksByUserWorkflow[userWorkflow.id] !== 'fetching' && hasAssignedTasksByUserWorkflow[userWorkflow.id];
  const needsAttention = userWorkflow.status === 'data-collection' ? Boolean(hasAssignedTasks && hasAssignedTasks !== 'fetching') : false;
  const currentInvoices = (userWorkflow?.invoices?.items || []).filter(invoice => invoice.status !== ApiModels.INVOICE_STATUS.EDITED);

  const cummulativeInvoice = currentInvoices.reduce((acc, invoice) => {
    acc.amountPaid += invoice.amountPaid;
    acc.amountTotal += invoice.amountTotal;
    acc.amountPending += invoice.amountPending;
    if (invoice.status !== ApiModels.INVOICE_STATUS.VOIDED && invoice.status !== ApiModels.INVOICE_STATUS.SKIPPED) {
      acc.amountDue += invoice.amountDue;
    }
    return acc;
  }, { amountDue: 0, amountPaid: 0, amountPending: 0, amountTotal: 0 });

  const cells: TableCell[] = [
    {
      cta: 'Open workflow',
      href: getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: userWorkflow.id }),
      icon: userWorkflow.tagIcon && userWorkflow.tagText && Emoji({ title: userWorkflow.tagText, unicode: userWorkflow.tagIcon }),
      label: userWorkflow.workflow.name,
      newTab: true,
      type: 'open-details',
    },
    statusBadge
      ? {
        type: 'badge',
        ...statusBadge,
      }
      : {
        type: 'empty',
      },
    {
      type: 'assignees',
      userWorkflowId: userWorkflow.id,
    },
    {
      content: InitiatedByPill({ initiatedBy: {
        color: userWorkflow.initiatedByPerson?.primaryColour ?? '',
        id: userWorkflow.initiatedByPerson?.id ?? '',
        logo: userWorkflow.initiatedByPerson?.profilePhoto?.url ?? '',
        name: userWorkflow.initiatedByPerson?.name ?? '',
      }}),
      type: 'component',
    },
    {
      content: userWorkflow.person.name || '—',
      type: 'text',
    },
    {
      content: InvoiceProgress({ invoice: cummulativeInvoice }),
      type: 'component',
    },
    {
      content: formatUserWorkflowContext(userWorkflow.context),
      type: 'text',
    },
    {
      content: `${userWorkflow.numPendingClientTasks}`,
      type: 'text',
    },
    typeof progress === 'number'
      ? {
        progress,
        type: 'progress',
      }
      : { type: 'empty' },
    {
      content: format.datetimeLocalTZ.withoutTimezone({ datetime: userWorkflow.lastActivityAt }),
      type: 'text',
    },
    {
      content: format.datetimeLocalTZ.withoutTimezone({ datetime: userWorkflow.createdAt }),
      type: 'text',
    },
  ];

  return {
    cells: cells.map(curriedAddHeaderKeyToCell),
    id: userWorkflow.id,
    state: needsAttention ? 'warning' : undefined,
  };
};

const getDetailPagePath: HookResponse['getDetailPagePath'] = ({ id }) => getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: id });

const useActivity = ({ resourceType }: { resourceType: ResourceType }): HookResponse => {
  const useTableView = useFeatureFlag('table-view');
  const pathParams = useParams<{ userWorkflowId?: string }>();
  const { creator, customStatuses } = useContext(DashboardContext);
  const creatorId = creator?.id;
  const [countsByStatus, setCountsByStatus] = useState<Record<string, number>>({});
  const [userWorkflows, setUserWorkflows] = useState<ThisUserWorkflow[]>([]);
  const [itemsCount, setItemsCount] = useState<number | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [teamMembers, setTeamMembers] = useState<Awaited<ReturnType<typeof tsClient.creators.getCreatorTeam>>['items']>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [selectedInitiatedMembers, setSelectedInitiatedMembers] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<{ startDate: string | null; endDate: string | null } | undefined>({
    endDate: null,
    startDate: null,
  });
  const [hasAssignedTasksByUserWorkflow, setHasAssignedTasksByUserWorkflow] = useState<Record<string, boolean | 'fetching'>>({});
  const [numPages, setNumPages] = useState(0);
  const [currentSortDirection, setCurrentSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentSortField, setCurrentSortField] = useState<string>('lastActivityAt');
  const mostRecentRequestRef = useRef<string | null>(null);
  const { getData: getUserWorkflowsFn } = useNoodleApi(tsClient.creators.getUserWorkflows);
  const { getData: getUserWorkflowProgressFn } = useNoodleApi(tsClient.workflows.getUserWorkflowProgress);
  const { getData: getTeamMembersFn } = useNoodleApi(tsClient.creators.getCreatorTeam);
  const [customTermsFilterValue, setCustomTermsFilterValue] = useQueryState('custom-terms');
  const [documentRequestUserFilterValue, setDocumentRequestUserFilterValue] = useQueryState('document-request-user');
  const [formRequestFilterValue, setFormRequestFilterValue] = useQueryState('form-request');
  const [invoiceFilterValue, setInvoiceFilterValue] = useQueryState('invoice');
  const [viewTable] = useQueryState('table');

  const { addListener, removeListener } = useEventsContext();
  const TABLE_COLUMNS_WITH_SORT = useMemo(
    () =>
      TABLE_COLUMNS.map(column => ({
        ...column,
        sort: column.columnId
          ? {
            currentSortByField: currentSortField,
            currentSortDirection,
            onSort: (columnId: string, direction: 'asc' | 'desc') => {
              setCurrentSortField(columnId);
              setCurrentSortDirection(direction);
            },
          }
          : undefined,
      })),
    [currentSortField, currentSortDirection, setCurrentSortField, setCurrentSortDirection],
  );

  const selectedClientTaskFilters = useMemo(() => {
    const allFilters: Record<PendingClientTasksFilter, string | null> = {
      'custom-terms': customTermsFilterValue,
      'document-request-user': documentRequestUserFilterValue,
      'form-request': formRequestFilterValue,
      invoice: invoiceFilterValue,
    };

    return Object.keys(allFilters).filter(key => allFilters[key as unknown as PendingClientTasksFilter] === 'true');
  }, [customTermsFilterValue, documentRequestUserFilterValue, formRequestFilterValue, invoiceFilterValue]);

  const [initiatedByPersonId] = useQueryState('initiatedByPersonId');

  const selectedId = pathParams?.userWorkflowId;

  const userWorkflowToListItem = ({
    hasAssignedTasksByUserWorkflow: hasAssignedTasksByAnUserWorkflow,
    userWorkflow,
    userWorkflowStatus,
  }: {
    hasAssignedTasksByUserWorkflow: Record<string, boolean | 'fetching'>;
    userWorkflow: Awaited<ReturnType<typeof tsClient.creators.getUserWorkflows>>['items'][number];
    userWorkflowStatus: ApiModels.CustomStatus | undefined;
  }): ListItem => {
    const hasAssignedTasks = hasAssignedTasksByAnUserWorkflow[userWorkflow.id] !== 'fetching' && hasAssignedTasksByAnUserWorkflow[userWorkflow.id];

    const currentInvoices = (userWorkflow?.invoices?.items || []).filter(invoice => invoice.status !== ApiModels.INVOICE_STATUS.EDITED);

    const cummulativeInvoice = currentInvoices.reduce((acc, invoice) => {
      acc.amountPaid += invoice.amountPaid;
      acc.amountTotal += invoice.amountTotal;
      acc.amountPending += invoice.amountPending;
      if (invoice.status !== ApiModels.INVOICE_STATUS.VOIDED && invoice.status !== ApiModels.INVOICE_STATUS.SKIPPED) {
        acc.amountDue += invoice.amountDue;
      }
      return acc;
    }, { amountDue: 0, amountPaid: 0, amountPending: 0, amountTotal: 0 });

    const getHref = (): string => {
      const queryParams = new URLSearchParams();
      if (customTermsFilterValue) queryParams.set('custom-terms', customTermsFilterValue);
      if (documentRequestUserFilterValue) queryParams.set('document-request-user', documentRequestUserFilterValue);
      if (formRequestFilterValue) queryParams.set('form-request', formRequestFilterValue);
      if (invoiceFilterValue) queryParams.set('invoice', invoiceFilterValue);
      return `${getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: userWorkflow.id })}?${queryParams.toString()}`;
    };

    return {
      badge: statusToBadge({ pausedUntil: userWorkflow.pausedUntil, status: userWorkflowStatus }),
      bottomLine: InvoiceProgress({ invoice: cummulativeInvoice, showBorder: false, workflowName: userWorkflow.workflow.name }),
      href: getHref(),
      id: userWorkflow.id,
      label: `${userWorkflow.title}`,
      labelComponents: [
        {
          component: LanguagePill({ compact: true, language: userWorkflow.person.preferredLanguage }),
          key: `language-${userWorkflow.person.preferredLanguage}`,
        },
        ...(userWorkflow.tagIcon && userWorkflow.tagText
          ? [{
            component: Emoji({ title: userWorkflow.tagText, unicode: userWorkflow.tagIcon }),
            key: `emoji-${userWorkflow.tagText}`,
          }]
          : []),
      ],
      linkedCells: {
        items: userWorkflow.associatedUserWorkflows.map(uw =>
          userWorkflowToListItem({
            hasAssignedTasksByUserWorkflow: hasAssignedTasksByAnUserWorkflow,
            userWorkflow: { ...uw, associatedUserWorkflows: [] },
            userWorkflowStatus,
          }),
        ),
        type: 'workflows',
      },
      messageCount: userWorkflow.messageCount,
      needsAttention: userWorkflow.status === 'data-collection' ? Boolean(hasAssignedTasks && hasAssignedTasks !== 'fetching') : undefined,
      progress: userWorkflow.status === 'data-collection' || userWorkflow.pausedUntil ? userWorkflow.progress * 100 : undefined,
      topLine: formatUserWorkflowContext(userWorkflow.context),
    };
  };

  const fetchWorkflows = useCallback(
    async ({
      initiateMemberIds,
      clientTaskFilters,
      page: newPage,
      search: newSearch,
      statuses,
      assignedTeamMemberIds,
      sortField,
      sortDirection,
    }: {
      initiateMemberIds: string[];
      clientTaskFilters: string[];
      page: number;
      search: string;
      statuses: string[];
      assignedTeamMemberIds: string[];
      sortField: string;
      sortDirection: 'asc' | 'desc';
    }): Promise<void> => {
      if (creatorId && resourceType === 'activity') {
        setCurrentPage(newPage);
        const thisRequestId = nanoid();
        mostRecentRequestRef.current = thisRequestId;
        setIsFetching(true);
        setUserWorkflows([]);
        setCurrentPage(newPage);

        const response = await getUserWorkflowsFn({
          assignedTeamMemberIds,
          clientTaskFilters,
          creatorId,
          endDate: dateFilter?.endDate,
          initiateMemberIds,
          initiatedByPersonId,
          page: newPage,
          perPage: 25,
          search: newSearch,
          sortDirection,
          sortField,
          startDate: dateFilter?.startDate,
          status: statuses,
        });

        if (thisRequestId === mostRecentRequestRef.current) {
          if (response.data) {
            setNumPages(response.data.numPages);
            setUserWorkflows(response.data.items);
            setItemsCount(response.data.numItems);
          }
          setIsFetching(false);
        }
      }
    },
    [creatorId, resourceType, getUserWorkflowsFn, initiatedByPersonId, dateFilter],
  );

  useEffect(() => {
    const fetchTeamMembers = async (): Promise<void> => {
      if (creatorId) {
        const { data } = await getTeamMembersFn({ creatorId, page: 1, perPage: 1_000 });
        setTeamMembers(data?.items || []);
      }
    };
    fetchTeamMembers();
  }, [creatorId, getTeamMembersFn]);

  useEffect(() => {
    if (currentSortField === 'progress') {
      setSelectedStatuses(['data-collection']);
    }
  }, [currentSortField]);

  useEffect(() => {
    if (!selectedStatuses.includes('data-collection') || selectedStatuses.length !== 1) {
      setCurrentSortField('lastActivityAt');
    }
  }, [selectedStatuses]);

  const handleNewPageRef = useRef((_newPage: number) => {});
  handleNewPageRef.current = (newPage: number): void => {
    fetchWorkflows({
      assignedTeamMemberIds: selectedTeamMembers,
      clientTaskFilters: selectedClientTaskFilters,
      initiateMemberIds: selectedInitiatedMembers,
      page: newPage,
      search: searchTerm,
      sortDirection: currentSortDirection,
      sortField: currentSortField,
      statuses: selectedStatuses,
    });
  };
  useEffect(() => {
    const listener = addListener('refreshUserWorkflowList', () => {
      handleNewPageRef.current(currentPage);
    });
    return () => removeListener('refreshUserWorkflowList', listener);
  }, [addListener, currentPage, removeListener]);

  useEffect(() => {
    fetchWorkflows({
      assignedTeamMemberIds: selectedTeamMembers,
      clientTaskFilters: selectedClientTaskFilters,
      initiateMemberIds: selectedInitiatedMembers,
      page: 1,
      search: searchTerm,
      sortDirection: currentSortDirection,
      sortField: currentSortField,
      statuses: selectedStatuses,
    });
  }, [
    fetchWorkflows,
    searchTerm,
    selectedStatuses,
    selectedTeamMembers,
    selectedInitiatedMembers,
    selectedClientTaskFilters,
    currentSortDirection,
    currentSortField,
  ]);

  const reloadListView = async (): Promise<void> => {
    fetchWorkflows({
      assignedTeamMemberIds: selectedTeamMembers,
      clientTaskFilters: selectedClientTaskFilters,
      initiateMemberIds: selectedInitiatedMembers,
      page: 1,
      search: searchTerm,
      sortDirection: currentSortDirection,
      sortField: currentSortField,
      statuses: selectedStatuses,
    });
  };

  useEffect(() => {
    const needProgressIds = userWorkflows
      .filter(userWorkflow => hasAssignedTasksByUserWorkflow[userWorkflow.id] === undefined)
      .map(userWorkflow => userWorkflow.id);

    Bluebird.mapSeries(needProgressIds, async userWorkflowId => {
      setHasAssignedTasksByUserWorkflow(prev => ({ ...prev, [userWorkflowId]: 'fetching' }));
      const response = await getUserWorkflowProgressFn({ userWorkflowId });
      if (response.data) {
        const responseData = response.data;
        setHasAssignedTasksByUserWorkflow(prev => ({ ...prev, [userWorkflowId]: responseData.hasAssignedTasks }));
      }
    });
    // intentionally not looking at hasAssignedTasksByUserWorkflow, only do this if the userWorkflows changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userWorkflows, getUserWorkflowProgressFn]);

  const pagination = {
    numPages,
    onChangePage: handleNewPageRef.current,
    page: currentPage,
  };

  useEffect(() => {
    const loadAllCounts = async (): Promise<void> => {
      if (creatorId && resourceType === 'activity' && customStatuses) {
        const getParams = {
          creatorId,
          initiatedByPersonId,
          page: 1,
          perPage: 1,
          search: searchTerm,
        };
        await Bluebird.map(
          [...customStatuses, { slug: 'paused' }],
          async status => {
            const response = await getUserWorkflowsFn({
              ...getParams,
              status: [status.slug],
            });
            setCountsByStatus(prev => ({ ...prev, [status.slug]: response.data?.numItems ?? 0 }));
          },
          { concurrency: 5 },
        );
      }
    };
    loadAllCounts();
  }, [customStatuses, creatorId, getUserWorkflowsFn, initiatedByPersonId, searchTerm, resourceType]);

  const filterItems = useMemo(() => {
    const statuses: Array<{
      isChecked: boolean;
      key: string;
      label: string;
    }> = [
      ...(customStatuses ?? []).map(status => ({
        isChecked: selectedStatuses.includes(status.slug),
        key: status.slug,
        label: `${status.title} (${countsByStatus[status.slug] ?? 0})`,
      })),
      {
        isChecked: selectedStatuses.includes('paused'),
        key: 'paused',
        label: `Paused (${countsByStatus.paused ?? 0})`,
      },
    ];
    return statuses;
  }, [
    customStatuses,
    selectedStatuses,
    countsByStatus,
  ]);

  const clientTaskFilterItems = useMemo(() => {
    const clientTaskTypes: Array<{
      isChecked: boolean;
      key: string;
      label: string;
    }> = [
      {
        isChecked: selectedClientTaskFilters.includes('document-request-user'),
        key: 'document-request-user',
        label: `Upload Document Requests`,
      },
      {
        isChecked: selectedClientTaskFilters.includes('custom-terms'),
        key: 'custom-terms',
        label: `Retainer Term Requests`,
      },
      {
        isChecked: selectedClientTaskFilters.includes('form-request'),
        key: 'form-request',
        label: `Form Requests`,
      },
      {
        isChecked: selectedClientTaskFilters.includes('invoice'),
        key: 'invoice',
        label: `Invoice Requests`,
      },
    ];
    return clientTaskTypes;
  }, [selectedClientTaskFilters]);

  const teamMemberFilterItems = useMemo(() => {
    const teamMemberTypes: Array<{
      isChecked: boolean;
      key: string;
      label: string;
    }> = teamMembers?.map(teamMember => ({
      isChecked: selectedTeamMembers.includes(teamMember.id),
      key: teamMember.id,
      label: teamMember.name || teamMember.id,
    })) || [];
    return teamMemberTypes;
  }, [selectedTeamMembers, teamMembers]);
  const teamMemberFilterItemsforInitiatedBy = useMemo(() => {
    const teamMemberTypes: Array<{
      isChecked: boolean;
      key: string;
      label: string;
    }> = teamMembers?.map(teamMember => ({
      isChecked: selectedInitiatedMembers.includes(teamMember.id),
      key: teamMember.id,
      label: teamMember.name || teamMember.id,
    })) || [];
    return teamMemberTypes;
  }, [selectedInitiatedMembers, teamMembers]);
  const filters: NonNullable<HookResponse['filters']> = {
    items: [
      {
        isMultiSelect: true,
        items: filterItems,
        label: 'Status',
        onChange: newStatuses => setSelectedStatuses(newStatuses),
      },
      {
        isMultiSelect: true,
        items: teamMemberFilterItems,
        label: 'Team member',
        onChange: newMembers => setSelectedTeamMembers(newMembers),
      },
      {
        isMultiSelect: true,
        items: clientTaskFilterItems,
        label: 'Pending Client Tasks',
        onChange: newFilters => {
          if (newFilters.includes('custom-terms')) {
            setCustomTermsFilterValue('true');
          } else {
            setCustomTermsFilterValue('false');
          }

          if (newFilters.includes('document-request-user')) {
            setDocumentRequestUserFilterValue('true');
          } else {
            setDocumentRequestUserFilterValue('false');
          }

          if (newFilters.includes('form-request')) {
            setFormRequestFilterValue('true');
          } else {
            setFormRequestFilterValue('false');
          }

          if (newFilters.includes('invoice')) {
            setInvoiceFilterValue('true');
          } else {
            setInvoiceFilterValue('false');
          }
        },
      },
      {
        isMultiSelect: true,
        items: [...teamMemberFilterItemsforInitiatedBy,
          {
            isChecked: selectedInitiatedMembers.includes('others'),
            key: 'others',
            label: 'Others',
          },
        ],
        label: 'Initiated By',
        onChange: (newMembers) => {
          if(newMembers.includes('others') && !selectedInitiatedMembers.includes('others')) {
            setSelectedInitiatedMembers(['others']);
          }else{
            setSelectedInitiatedMembers(newMembers.filter((m) => m!=='others'));
          }
        },
      },
    ],
  };

  const listItems = userWorkflows.map(userWorkflow => {
    const userWorkflowStatus = customStatuses?.find(status => status.slug === userWorkflow.status) ?? undefined;
    return userWorkflowToListItem({
      hasAssignedTasksByUserWorkflow,
      userWorkflow,
      userWorkflowStatus,
    });
  });

  const tableRows = useMemo(
    () =>
      userWorkflows.map(userWorkflow =>
        userWorkflowToTableRow({
          hasAssignedTasksByUserWorkflow,
          userWorkflow,
          userWorkflowStatuses: customStatuses,
        }),
      ),
    [userWorkflows, hasAssignedTasksByUserWorkflow, customStatuses],
  );

  return {
    dateFilter,
    filters,
    getDetailPagePath,
    ifNoItemsText: 'No activity',
    isFetchingList: isFetching,
    items: listItems,
    itemsCount,
    onSearch: setSearchTerm,
    openTableView: viewTable === 'true',
    pagination,
    reloadListView,
    selectedId,
    setDateFilter,
    tableView: useTableView
      ? {
        columns: TABLE_COLUMNS_WITH_SORT,
        rows: tableRows,
      }
      : undefined,
  };
};

export default useActivity;
